import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Modal, Row, Spinner } from "react-bootstrap";
import { Form, Formik } from "formik";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarDays,
  faCircleCheck,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import axios from "src/config/axios";
import FormInputText from "src/components/Forms/FormInputText";
import FormSelectBox from "src/components/Forms/FormSelectBox";
import CategoryIcon from "src/components/CategoryIcon";
import { dateFormatter } from "src/global";
import {
  showErrorToast,
  showValidationErrorToast,
} from "src/components/Toasts";

const ParkingSlotLabel = ({ parkingSlot }) => {
  if (!parkingSlot) {
    return "-";
  }

  return (
    <div className="d-flex align-items-center">
      {parkingSlot.category && (
        <CategoryIcon category={parkingSlot.category} size={24} />
      )}
      <span className={parkingSlot.category ? "ml-2" : ""}>
        {parkingSlot.category ? `${parkingSlot.category.name} ` : ""}
        {parkingSlot.name}
      </span>
    </div>
  );
};

const SplitBookingDialog = ({ booking, stayId, handleClose, handleSubmit }) => {
  const [parkingSlots, setParkingSlots] = useState([]);
  const [parkingSlotsLoading, setParkingSlotsLoading] = useState(false);
  const formRef = useRef();
  const { t } = useTranslation("dashboard");

  const minSplitDate = moment(booking.booked_from)
    .add(1, "day")
    .format(moment.HTML5_FMT.DATE);
  const maxSplitDate = moment(booking.booked_until)
    .subtract(1, "day")
    .format(moment.HTML5_FMT.DATE);

  function splitDateTime(splitDate) {
    const selectedDate = moment(splitDate, moment.HTML5_FMT.DATE);

    return moment(booking.booked_from)
      .year(selectedDate.year())
      .month(selectedDate.month())
      .date(selectedDate.date())
      .format();
  }

  async function loadParkingSlots(splitDate) {
    if (!splitDate) {
      return;
    }

    setParkingSlotsLoading(true);

    await axios
      .get("/api/v1/parking_slots.json", {
        params: {
          ignore_occupancy_of_stay_id: stayId,
          from: splitDateTime(splitDate),
          to: booking.booked_until,
          active: true,
        },
      })
      .then(({ data }) => {
        setParkingSlots(
          data.entries.map((parkingSlot) => ({
            ...parkingSlot,
            display_name: `${parkingSlot.name} (${
              parkingSlot.is_available
                ? t("booking_details.booking_section.split_dialog.available")
                : t("booking_details.booking_section.split_dialog.occupied")
            })`,
          }))
        );
      })
      .catch(() => {
        showErrorToast();
      })
      .finally(() => {
        setParkingSlotsLoading(false);
      });
  }

  useEffect(() => {
    loadParkingSlots(minSplitDate);
  }, []);

  async function submit(values, { setSubmitting }) {
    setSubmitting(true);

    await axios
      .post(`/api/v1/bookings/${booking.id}/split.json`, {
        booking_split: {
          split_at: values.split_at,
          parking_slot_id: values.parking_slot_id,
        },
      })
      .then(({ data }) => {
        handleSubmit(data);
      })
      .catch((error) => {
        showValidationErrorToast(error);
        setSubmitting(false);
      });
  }

  function handleSplitDateChange(event, setFieldValue) {
    const splitDate = event.target.value;
    setFieldValue("split_at", splitDate, true);
    loadParkingSlots(splitDate);
  }

  return (
    <Modal show={true} onHide={handleClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          {t("booking_details.booking_section.split_dialog.title")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted">
          {t("booking_details.booking_section.split_dialog.description")}
        </p>

        <div className="border rounded p-3 mb-4 bg-light">
          <div className="d-flex align-items-start">
            <FontAwesomeIcon
              icon={faCalendarDays}
              size="2x"
              className="text-primary mr-3 mt-1"
            />
            <div>
              <div className="font-weight-bold text-primary mb-1">
                {t(
                  "booking_details.booking_section.split_dialog.current_booking"
                )}
              </div>
              <ParkingSlotLabel parkingSlot={booking.parking_slot} />
              <div>
                {dateFormatter(booking.booked_from)} –{" "}
                {dateFormatter(booking.booked_until)} (
                {t("booking_details.booking_section.nights", {
                  count: booking.nights,
                })}
                )
              </div>
            </div>
          </div>
        </div>

        <Formik
          innerRef={formRef}
          initialValues={{
            split_at: minSplitDate,
            parking_slot_id: "",
          }}
          validationSchema={Yup.object({
            split_at: Yup.string()
              .required(
                t("booking_details.booking_section.split_dialog.required")
              )
              .test(
                "within-booking",
                t(
                  "booking_details.booking_section.split_dialog.invalid_split_date"
                ),
                (value) =>
                  value &&
                  moment(value).isBetween(
                    moment(booking.booked_from),
                    moment(booking.booked_until),
                    "day",
                    "()"
                  )
              ),
            parking_slot_id: Yup.string().required(
              t("booking_details.booking_section.split_dialog.required")
            ),
          })}
          onSubmit={submit}
        >
          {({ values, setFieldValue }) => {
            const selectedParkingSlot = parkingSlots.find(
              (parkingSlot) => parkingSlot.id === Number(values.parking_slot_id)
            );
            const firstBookingNights = values.split_at
              ? moment(values.split_at)
                  .startOf("day")
                  .diff(moment(booking.booked_from).startOf("day"), "days")
              : 0;
            const splitBookingNights = values.split_at
              ? moment(booking.booked_until)
                  .startOf("day")
                  .diff(moment(values.split_at).startOf("day"), "days")
              : 0;

            return (
              <Form autoComplete="false">
                <Row>
                  <Col md={6}>
                    <FormInputText
                      name="split_at"
                      label={t(
                        "booking_details.booking_section.split_dialog.split_at"
                      )}
                      hint={t(
                        "booking_details.booking_section.split_dialog.split_at_hint"
                      )}
                      type="date"
                      min={minSplitDate}
                      max={maxSplitDate}
                      required
                      onChange={(event) =>
                        handleSplitDateChange(event, setFieldValue)
                      }
                    />
                  </Col>
                  <Col md={6}>
                    <FormSelectBox
                      name="parking_slot_id"
                      label={t(
                        "booking_details.booking_section.split_dialog.new_parking_slot"
                      )}
                      options={parkingSlots}
                      option_name="display_name"
                      option_value="id"
                      loading={parkingSlotsLoading}
                      required
                    />
                  </Col>
                </Row>

                {selectedParkingSlot && (
                  <div className="border rounded p-3 mb-4">
                    <div className="mb-2">
                      {t(
                        "booking_details.booking_section.split_dialog.availability"
                      )}
                    </div>
                    <div
                      className={
                        selectedParkingSlot.is_available
                          ? "text-success"
                          : "text-warning"
                      }
                    >
                      <FontAwesomeIcon
                        icon={
                          selectedParkingSlot.is_available
                            ? faCircleCheck
                            : faTriangleExclamation
                        }
                        className="mr-2"
                      />
                      {t(
                        selectedParkingSlot.is_available
                          ? "booking_details.booking_section.split_dialog.parking_slot_available"
                          : "booking_details.booking_section.split_dialog.parking_slot_occupied",
                        {
                          parkingSlot: selectedParkingSlot.name,
                          from: dateFormatter(values.split_at),
                          to: dateFormatter(booking.booked_until),
                        }
                      )}
                    </div>
                  </div>
                )}

                <div>
                  <div className="mb-2">
                    {t("booking_details.booking_section.split_dialog.preview")}
                  </div>
                  <div className="table-responsive">
                    <table className="table table-bordered mb-0">
                      <thead>
                        <tr>
                          <th>
                            {t(
                              "booking_details.booking_section.split_dialog.period"
                            )}
                          </th>
                          <th>
                            {t(
                              "booking_details.booking_section.split_dialog.parking_slot"
                            )}
                          </th>
                          <th>
                            {t(
                              "booking_details.booking_section.split_dialog.nights"
                            )}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            <FontAwesomeIcon
                              icon={faCalendarDays}
                              className="mr-2"
                            />
                            {dateFormatter(booking.booked_from)} –{" "}
                            {dateFormatter(values.split_at)}
                          </td>
                          <td>
                            <ParkingSlotLabel
                              parkingSlot={booking.parking_slot}
                            />
                          </td>
                          <td>{firstBookingNights}</td>
                        </tr>
                        <tr>
                          <td>
                            <FontAwesomeIcon
                              icon={faCalendarDays}
                              className="mr-2"
                            />
                            {dateFormatter(values.split_at)} –{" "}
                            {dateFormatter(booking.booked_until)}
                          </td>
                          <td>
                            <ParkingSlotLabel
                              parkingSlot={selectedParkingSlot}
                            />
                          </td>
                          <td>{splitBookingNights}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          {t("booking_details.booking_section.split_dialog.cancel")}
        </Button>
        <Button
          onClick={() => formRef.current?.handleSubmit()}
          disabled={formRef.current?.isSubmitting || parkingSlotsLoading}
        >
          {formRef.current?.isSubmitting && (
            <Spinner animation="border" size="sm" className="mr-2" />
          )}
          {t("booking_details.booking_section.split_dialog.submit")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SplitBookingDialog;
